<template>
    <div class="background-small">
        <div class="header-section">
            <h4 class="text-white">Support Center</h4>
            <button class="create-ticket-btn" @click="showCreateTicketModal = true">
                <i class="fas fa-plus"></i>
                Create New Ticket
            </button>
        </div>

        <div class="tickets-container">
            <div class="tickets-list" v-if="tickets.length > 0">
                <div v-for="ticket in tickets" :key="ticket.id" class="ticket-card"
                    @click="goToTicketDetail(ticket.id)">
                    <div class="ticket-header">
                        <div class="ticket-main-info">
                            <div class="ticket-id-date">
                                <span class="ticket-id">#{{ ticket.ticket_number }}</span>
                                <span class="ticket-date">{{ formatDate(ticket.created_at) }}</span>
                            </div>
                            <div class="ticket-title-section">
                                <h6 class="ticket-title">{{ ticket.title }}</h6>
                                <span class="ticket-type" :class="ticket.type.toLowerCase()">
                                    {{ formatType(ticket.type) }}
                                </span>
                            </div>
                            <p class="ticket-description-preview">{{ ticket.description }}</p>
                        </div>
                        <div class="ticket-status-section">
                            <span class="priority-badge" :class="ticket.priority.toLowerCase()">
                                {{ ticket.priority }}
                            </span>
                            <span class="status-badge" :class="ticket.status.toLowerCase()">
                                {{ formatStatus(ticket.status) }}
                            </span>
                            <i class="fas fa-chevron-right"></i>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="empty-state">
                <i class="fas fa-ticket-alt empty-icon"></i>
                <p>No support tickets found</p>
            </div>
        </div>

        <div v-if="showCreateTicketModal" class="modal-overlay" @click.self="showCreateTicketModal = false">
            <div class="modal-content">
                <div class="modal-header">
                    <h5>Create New Support Ticket</h5>
                    <button class="close-btn" @click="showCreateTicketModal = false">&times;</button>
                </div>
                <div class="modal-body">
                    <form @submit.prevent="createTicket">
                        <div class="form-group">
                            <label>Title</label>
                            <input type="text" v-model="newTicket.title" placeholder="Enter ticket title" required>
                        </div>
                        <div class="form-group">
                            <label>Type</label>
                            <select v-model="newTicket.type" required>
                                <option value="">Select ticket type</option>
                                <option v-for="type in ticketTypes" :key="type" :value="type">
                                    {{ formatType(type) }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Priority</label>
                            <select v-model="newTicket.priority">
                                <option v-for="priority in ticketPriorities" :key="priority" :value="priority">
                                    {{ priority }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Description</label>
                            <textarea v-model="newTicket.description" placeholder="Describe your issue in detail..."
                                rows="5" required></textarea>
                        </div>
                        <div class="form-actions">
                            <button type="button" class="cancel-btn"
                                @click="showCreateTicketModal = false">Cancel</button>
                            <button type="submit" class="submit-btn">Create Ticket</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <InfoModal v-if="infoModal.show" :duration="infoModal.duration" :title="infoModal.title"
            :message="infoModal.message" :type="infoModal.type" @close="infoModal.show = false" />
    </div>
</template>

<script>
import DateUtils from "@/utils/DateUtils.js";
import SupportService from "@/services/SupportService.js";
import InfoModal from "@/components/modal/InfoModal.vue";

const TICKET_TYPES = {
    GENERAL: 'General Support',
    PAYMENT: 'Payment Issue',
    TECHNICAL: 'Technical Support',
    BILLING: 'Billing Issue',
    SUPPORT: 'Customer Support',
    COMPLAINT: 'Complaint',
    FEATURE_REQUEST: 'Feature Request',
    BUG_REPORT: 'Bug Report'
};

const TICKET_STATUSES = {
    ALL: 'All Tickets',
    OPEN: 'Open',
    IN_PROGRESS: 'In Progress',
    WAITING_USER_RESPONSE: 'Waiting for Response',
    PENDING: 'Pending',
    RESOLVED: 'Resolved',
    CLOSED: 'Closed',
    CANCELLED: 'Cancelled'
};

export default {
    name: 'SupportView',
    components: {
        InfoModal
    },
    directives: {
        'click-outside': {
            mounted(el, binding) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.addEventListener('click', el.clickOutsideEvent);
            },
            unmounted(el) {
                document.removeEventListener('click', el.clickOutsideEvent);
            }
        }
    },
    data() {
        return {
            tickets: [],
            searchQuery: '',
            selectedStatus: 'ALL',
            page: 1,
            limit: 20,
            showCreateTicketModal: false,
            statusOptions: ['ALL', 'OPEN', 'IN_PROGRESS', 'WAITING_USER_RESPONSE', 'PENDING', 'RESOLVED', 'CLOSED', 'CANCELLED'],
            ticketTypes: Object.keys(TICKET_TYPES),
            ticketPriorities: ['LOW', 'MEDIUM', 'HIGH', 'URGENT'],
            newTicket: {
                title: '',
                type: '',
                priority: 'MEDIUM',
                description: ''
            },
            infoModal: {
                show: false,
                title: '',
                message: '',
                type: 'info',
                duration: "4s",
                durationSeconds: 4000
            }
        }
    },
    methods: {
        formatType(type) {
            return TICKET_TYPES[type] || type;
        },
        formatStatus(status) {
            if (status === 'ALL') return 'All Tickets';
            return TICKET_STATUSES[status] || status;
        },
        showInfo(title, message, type = 'info') {
            this.infoModal = {
                show: true,
                title,
                message,
                type,
                duration: "4s",
                durationSeconds: 4000
            };
            this.infoModal.show = true;
            setTimeout(() => {
                this.infoModal.show = false;
            }, this.infoModal.durationSeconds);
        },
        formatDate(date) {
            return DateUtils.getDate(date);
        },
        goToTicketDetail(ticketId) {
            this.$router.push(`/support/ticket/${ticketId}`);
        },
        async createTicket() {
            const response = await SupportService.createTicket(this.newTicket);
            if (!response) return this.showInfo('Error', 'Failed to create ticket', 'error');

            this.showInfo('Success', 'Ticket created successfully', 'info');
            this.showCreateTicketModal = false;
            this.loadTickets();
            this.newTicket = {
                title: '',
                type: '',
                priority: 'MEDIUM',
                description: ''
            };
        },
        async loadTickets() {

            const items = await SupportService.getTickets({
                search: this.searchQuery,
                status: this.selectedStatus,
                priority: 'ALL',
                type: 'ALL',
                page: this.page,
                limit: this.limit,
                include_user: "false"
            });

            if (!items) return this.tickets = [];
            this.tickets = items.tickets.map(ticket => ({
                ...ticket,
                isExpanded: false,
                replyText: ''
            }));
        },
        closeDropdown() {
            this.isOpen = false;
        },
        selectStatus(status) {
            this.selectedStatus = status;
            this.isOpen = false;
        },
        toggleDropdown() {
            this.isOpen = !this.isOpen;
        }
    },
    async created() {
        this.loadTickets();
    }
}
</script>

<style scoped>
.support-container {
    padding: 1.5rem;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
}

.create-ticket-btn {
    background-image: linear-gradient(to right,
            #954ce9 0%,
            #7d32d2 51%,
            #5b28f3 100%);
    border: none;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
}

.create-ticket-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(149, 76, 233, 0.2);
}

.tickets-container {
    background: var(--secondary-color);
    border-radius: 12px;
    overflow: hidden;
}

.tickets-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.ticket-card {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
    padding: 0.75rem;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.ticket-card:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(149, 76, 233, 0.3);
}

.ticket-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1.5rem;
}

.ticket-main-info {
    flex: 1;
}

.ticket-id-date {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.75rem;
}

.ticket-id {
    color: #666;
    font-size: 0.9rem;
    font-family: monospace;
}

.ticket-date {
    color: #666;
    font-size: 0.8rem;
}

.ticket-title-section {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
}

.ticket-title {
    color: white;
    margin: 0;
    font-size: 1.1rem;
    font-weight: 500;
}

.ticket-type {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    background: rgba(149, 76, 233, 0.1);
    color: #954ce9;
    text-transform: uppercase;
}

.ticket-description-preview {
    color: #999;
    font-size: 0.9rem;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.ticket-status-section {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.status-badge,
.priority-badge {
    padding: 0.35rem 0.75rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
}


.empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: #666;
}

.empty-icon {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    color: #954ce9;
    opacity: 0.5;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.99);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: var(--secondary-color);
    border-radius: 12px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow: hidden;
}

.modal-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header h5 {
    color: white;
    margin: 0;
    font-size: 1.25rem;
}

.close-btn {
    background: none;
    border: none;
    color: #666;
    font-size: 1.5rem;
    cursor: pointer;
}

.modal-body {
    padding: 1.5rem;
}

.form-group {
    margin-bottom: 1.5rem;
}

.form-group label {
    display: block;
    color: white;
    margin-bottom: 0.5rem;
}

.form-group input,
.form-group select,
.form-group textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: white;
}

.form-group option {
    color: black;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
}

.cancel-btn {
    padding: 0.75rem 1.5rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    background: transparent;
    color: white;
    cursor: pointer;
}

.submit-btn {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    background: #10b981;
    color: white;
    cursor: pointer;
}

@media (max-width: 768px) {
    .tickets-container {
        padding: 0.75rem;
    }

    .ticket-card {
        padding: 1rem;
    }

    .ticket-header {
        flex-direction: column;
        gap: 1rem;
    }

    .ticket-status-section {
        width: 100%;
        justify-content: flex-start;
        gap: 0.5rem;
        flex-wrap: wrap;
    }

    .ticket-title {
        font-size: 1rem;
    }

    .header-section {
        padding: 0 1rem;
        margin-bottom: 1.5rem;
        flex-direction: column;
        gap: 1rem;
    }

    .create-ticket-btn {
        width: 100%;
        justify-content: center;
    }
}
</style>